import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FlattenedLabel } from '@pixacare/dtos';
import { select } from '@pixacare/shared';
import { LabelHttpService } from '@services/http/label.http.service';
import { AutoCompleteLabelModel } from '@shared/models/helpers/auto-complete-label-model';
import { isLabel } from '@shared/models/helpers/label-helper';
import { LabelComponent } from '../label/label.component';

const FAVORITE_LABELS_COUNT = 5;

@Component({
  selector: 'pxc-favorite-labels',
  templateUrl: './favorite-labels.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LabelComponent],
  host: {
    class: 'flex gap-2',
    '[attr.data-testid]': "areLabelsLoaded() ? 'favorite-labels' : 'favorite-labels-skeleton'",
  },
})
export class FavoriteLabelsComponent {
  private readonly labelService = inject(LabelHttpService);

  readonly selectedLabels = input<AutoCompleteLabelModel[]>([]);
  readonly labelPicked = output<AutoCompleteLabelModel>();

  private readonly flattenedLabels = toSignal(this.labelService.getFlattenedLabels());
  readonly areLabelsLoaded = computed(() => this.flattenedLabels() !== undefined);

  readonly skeletonPlaceholders = Array.from({ length: FAVORITE_LABELS_COUNT });

  readonly favoriteLabels = computed(() => {
    const selectedLabelIds = this.selectedLabels()
      .map((selectedLabel) => selectedLabel.value)
      .filter(isLabel)
      .map((label) => label.id);

    const sortedLabels = [...(this.flattenedLabels() ?? [])].sort(
      (first, second) => (second.userLabelStatsInstance?.count || 0) - (first.userLabelStatsInstance?.count || 0),
    );

    return select(
      sortedLabels,
      (label: FlattenedLabel) => !selectedLabelIds.includes(label.labelInstance.id),
      FAVORITE_LABELS_COUNT,
    ).map((flattenedLabel) => ({
      display: flattenedLabel.labelInstance.word,
      value: flattenedLabel.labelInstance,
    }));
  });
}
